import packageJson from '../../../package.json'

import Axios, {
  AxiosResponse,
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from 'axios'

export const defaultHeaders = {
  'Content-Type': 'application/json',
}

export type InterceptorPromise = string | void | null
export const tokenInterceptor = async (
  config: InternalAxiosRequestConfig,
  fn: () => Promise<InterceptorPromise> | null,
) => {
  // App-Version
  const PackageVersion = packageJson.version
  config.headers['App-Version'] = PackageVersion

  const token = await fn()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
}

class ApiClient {
  public axiosInstance = Axios.create({
    baseURL: process.env.REACT_APP_BASE_API_URL,
    headers: defaultHeaders,
    timeout: 30000,
  })

  public tokenInterceptorId: number | null = null

  constructor() {
    this.tokenInterceptorId = null
  }

  public setAxiosInstance(_axiosInstance: AxiosInstance) {
    this.axiosInstance = _axiosInstance
  }

  public setDefaultToken(token: InterceptorPromise) {
    this.axiosInstance.defaults.headers.Authorization = `Bearer ${token}`
  }

  public async setGetAccessToken(getAccessToken: () => Promise<InterceptorPromise>) {
    if (this.tokenInterceptorId) {
      this.axiosInstance.interceptors.request.eject(this.tokenInterceptorId)
    }
    this.tokenInterceptorId = await this.axiosInstance.interceptors.request.use((config) =>
      tokenInterceptor(config, getAccessToken),
    )
  }

  public isTokenInterceptorEnabled() {
    return !!this.tokenInterceptorId
  }

  public async get<T, U>(url: string, params?: U): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, { ...params })
  }

  public async post<T, U>(
    url: string,
    data?: U,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config)
  }

  public async put<T, U>(url: string, data?: U): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data)
  }

  public async delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url)
  }
}

export default ApiClient
